// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import { useLocation, Route, RouteComponentProps, Switch, Redirect } from 'wouter';

import { defined } from '@simlin/core/common';
import { HostedWebEditor } from '@simlin/diagram/HostedWebEditor';

import Home from './Home';
import { Login } from './Login';
import { NewUser } from './NewUser';
import { User } from './User';
import { getSession, signOut, SessionUser } from './auth-client';
import { loadPreferences } from './preferences';
import { createMerlinProposer } from './merlin-client';
import { createAthenaDescriber } from './athena-client';

import styles from './App.module.css';

// WS4: one shared Merlin proposer + Athena describer for the hosted editor --
// POST to the same-origin /api/merlin/propose and /api/athena/describe (the
// server runs the agent cores + tier gating).
const merlinProposer = createMerlinProposer();
const athenaDescriber = createAthenaDescriber();

interface EditorMatchParams {
  username: string;
  projectName: string;

  readonly [paramName: string | number]: string | undefined;
}

// Map the NextAuth session user onto the app's User. Only called once the user
// has a username (post-onboarding), so `id` -- which the app uses as the owner
// handle in project paths -- is always the username.
function userFromSession(u: SessionUser): User {
  return {
    id: u.username ?? '',
    displayName: u.name || u.username || u.email || '',
    email: u.email ?? '',
    photoUrl: u.image,
    tier: u.tier,
  };
}

// Bootstrap states: 'loading' until the first session fetch resolves, then
// logged-out (no session), new (signed in but no username yet -> onboarding), or
// ready (fully signed-in user).
type AuthStatus = 'loading' | 'loggedOut' | 'newUser' | 'ready';

interface AppState {
  status: AuthStatus;
  user?: User;
}

class InnerApp extends React.PureComponent<{}, AppState> {
  state: AppState = { status: 'loading' };

  componentDidMount(): void {
    void this.loadSession();
  }

  // Fetch the current Auth.js session and derive the bootstrap state. Re-run
  // after onboarding: the server refreshes the JWT's username on the next
  // session read, so this resolves to 'ready' once a username is assigned.
  loadSession = async (): Promise<void> => {
    const session = await getSession();
    if (!session) {
      this.setState({ status: 'loggedOut', user: undefined });
      return;
    }
    if (!session.user.username) {
      this.setState({ status: 'newUser', user: undefined });
      return;
    }
    this.setState({ status: 'ready', user: userFromSession(session.user) });
  };

  handleUsernameChanged = (): void => {
    void this.loadSession();
  };

  editor = (props: RouteComponentProps<EditorMatchParams>) => {
    const { username, projectName } = props.params;
    const user = this.state.user;
    const readOnlyMode = !user || user.id !== username;
    // wouter's useLocation()[1] is the imperative navigator; calling it
    // with '/' returns the user to the dashboard route owned by
    // Home.tsx. Keeps the editor package routing-agnostic — the diagram
    // toolbar's Home button just invokes whatever callback the host
    // provides.
    const [, setLocation] = useLocation();

    // CO-3: hydrate the editor from the local-first Settings preferences. Read
    // at mount time -- changing Settings then reopening a model applies them,
    // matching the theme's local-first behavior.
    const prefs = loadPreferences(typeof window !== 'undefined' ? window.localStorage : undefined);

    return (
      <HostedWebEditor
        username={username}
        projectName={projectName}
        baseURL={this.getBaseURL()}
        readOnlyMode={readOnlyMode}
        onHome={() => setLocation('/')}
        initialViewMode={prefs.defaultView}
        showCanvasGrid={prefs.canvasGrid}
        canvasPaper={prefs.canvasPaper}
        propose={merlinProposer}
        describe={athenaDescriber}
        agentTier={this.state.user?.tier}
      />
    );
  };

  getBaseURL(): string {
    return '';
  }

  // Sign out through Auth.js: signOut() navigates the browser to '/', which
  // clears the NextAuth session cookie server-side and reloads the app into the
  // logged-out state. No local state juggling needed -- unlike the old Firebase
  // path, the server session really is destroyed.
  handleLogout = async (): Promise<void> => {
    await signOut('/');
  };

  home = (_props: RouteComponentProps) => {
    const location = useLocation()[0];

    const isNewProject = location === '/new';
    return <Home isNewProject={isNewProject} user={defined(this.state.user)} onLogout={this.handleLogout} />;
  };

  render() {
    const urlParams = new URLSearchParams(window.location.search);
    const projectParam = urlParams.get('project');
    if (projectParam) return <Redirect to={projectParam} />;

    // A password-reset email links to /?reset_token=…; surface it to Login so it
    // opens on the set-new-password form.
    const resetToken = urlParams.get('reset_token') ?? undefined;

    // if a user is navigating to a project, skip the high-level auth check so
    // public models render for anyone (the API enforces access for private ones).
    if (!/\/.*\/.*/.test(window.location.pathname)) {
      if (this.state.status === 'loading' || this.state.status === 'loggedOut') {
        return <Login disabled={this.state.status === 'loading'} initialResetToken={resetToken} />;
      }

      if (this.state.status === 'newUser') {
        return <NewUser onUsernameChanged={this.handleUsernameChanged} />;
      }
    }

    return (
      <React.Fragment>
        <Switch>
          <div className={styles.inner}>
            <Route path="/" component={this.home} />
            <Route path="/:username/:projectName" component={this.editor} />
            <Route path="/new" component={this.home} />
          </div>
        </Switch>
      </React.Fragment>
    );
  }
}

export class App extends React.PureComponent {
  render(): React.JSX.Element {
    return (
      <React.StrictMode>
        <InnerApp />
      </React.StrictMode>
    );
  }
}
